import * as THREE from "three";

/**
 * Скачок в 3D — превращение в пыльцу (Джон 04.11.2026, 21:26: «он в пыльцу трансформируется, сжимается, и потом она
 * лёгким ветерком… с другой стороны разжимается»; 21:27 — рой частиц, как превращение машин в кино; берём принцип).
 * Частицы — точки поверхности самой модели: мелкие светящиеся чешуйки с бликом. Фазы по доле скачка:
 *   замах — с тела осыпаются чешуйки (видно, что будет скачок, можно закрыться);
 *   сжатие — рой стягивается в маленький яркий шар у груди;
 *   переход — шар со слабым шлейфом огибает соперника по глубине, за его спиной;
 *   раскрытие — шар распускается, рой ложится слоями от центра к контуру — боец лицом к сопернику.
 * Всё считается от кадра способности и положений мира: при откате и в записи картинка та же.
 */
export interface DustJump {
  /** Кадр способности и границы фаз (кадры): начало растворения и конец сборки. */
  readonly frame: number;
  readonly vanishStart: number;
  readonly vanishEnd: number;
  /** Откуда и куда (стопы, пиксели мира), куда смотрел в начале и в конце. */
  readonly fromX: number;
  readonly toX: number;
  readonly fromFacing: number;
  readonly toFacing: number;
  /** Где соперник — шар огибает его сзади. */
  readonly opponentX: number;
}

/** Доли фаз внутри окна растворения (0..1): сжатие, переход, раскрытие. */
const SHRINK_END = 0.28;
const TRAVEL_END = 0.68;
/** Высота груди, радиус шара, глубина обхода (за соперником), пиксели мира. */
const CHEST = 100;
const BALL = 7;
const BEHIND = 150;

const hash = (value: number): number => {
  let h = Math.imul(value ^ 0x9e3779b9, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
};
const ease = (u: number): number => (u <= 0 ? 0 : u >= 1 ? 1 : u * u * (3 - 2 * u));
const clamp01 = (u: number): number => Math.min(1, Math.max(0, u));

export class DustSwarm {
  readonly points: THREE.Points;
  private readonly local: Float32Array;
  private readonly colors: Float32Array;
  private readonly positions: Float32Array;
  private readonly count: number;

  /** local — точки поверхности модели в её системе (стопы в нуле, лицом в +Z), body — цвет бойца. */
  constructor(local: Float32Array, body: THREE.Color) {
    this.local = local;
    this.count = local.length / 3;
    this.positions = new Float32Array(local.length);
    this.colors = new Float32Array(local.length);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(this.colors, 3));
    const material = new THREE.PointsMaterial({
      size: 3.2,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.points = new THREE.Points(geometry, material);
    this.points.frustumCulled = false;
    this.points.visible = false;
    this.body = body;
  }

  private readonly body: THREE.Color;

  /** Точка тела i в мире: стопы в x, лицом по facing (поворот вокруг вертикали, как у модели). */
  private bodyPoint(index: number, x: number, facing: number, out: THREE.Vector3): THREE.Vector3 {
    const lx = this.local[index * 3] ?? 0;
    const ly = this.local[index * 3 + 1] ?? 0;
    const lz = this.local[index * 3 + 2] ?? 0;
    // модель лицом в +Z; повёрнута к facing: +Z → +X·facing
    return out.set(x + lz * facing, ly, -lx * facing);
  }

  /** Обновить рой на кадр мира; jump — null, когда скачка нет. */
  update(jump: DustJump | null, tick: number): void {
    if (!jump) {
      this.points.visible = false;
      return;
    }
    this.points.visible = true;
    const span = Math.max(1, jump.vanishEnd - jump.vanishStart);
    const u = (jump.frame - jump.vanishStart) / span; // < 0 — замах
    const from = new THREE.Vector3();
    const to = new THREE.Vector3();
    const chestFrom = new THREE.Vector3(jump.fromX, CHEST, 0);
    const chestTo = new THREE.Vector3(jump.toX, CHEST, 0);
    // путь шара: по полу от точки к точке, по глубине — за спиной соперника (дуга в −Z)
    const travel = ease(clamp01((u - SHRINK_END) / (TRAVEL_END - SHRINK_END)));
    const ball = new THREE.Vector3(
      chestFrom.x + (chestTo.x - chestFrom.x) * travel,
      CHEST + Math.sin(Math.PI * travel) * 12,
      -Math.sin(Math.PI * travel) * BEHIND,
    );
    const color = new THREE.Color();
    for (let index = 0; index < this.count; index++) {
      const seed = hash(index * 2654435761);
      const jitter = (seed & 1023) / 1023;
      const p = new THREE.Vector3();
      if (u < 0) {
        // замах: тело на месте, часть чешуек осыпается и мерцает
        const warm = clamp01(1 + u * 3); // последние трети замаха
        this.bodyPoint(index, jump.fromX, jump.fromFacing, p);
        if (jitter < warm * 0.5) {
          p.y -= warm * warm * ((seed >>> 10) & 31);
          p.x += (((seed >>> 16) & 31) - 15) * warm * 0.5;
        } else p.set(0, -9999, 0);
      } else if (u < SHRINK_END) {
        // сжатие: волной от контура к груди, с закруткой
        const s = ease(clamp01(u / SHRINK_END - jitter * 0.35));
        this.bodyPoint(index, jump.fromX, jump.fromFacing, from);
        const target = ballPoint(chestFrom, seed, BALL);
        p.lerpVectors(from, target, s);
        const swirl = s * (1 - s) * 30;
        p.x += Math.cos(seed + s * 9) * swirl;
        p.z += Math.sin(seed + s * 9) * swirl;
      } else if (u < TRAVEL_END) {
        // переход: шар и «ветерок» — часть точек отстаёт шлейфом по пути
        const lag = jitter < 0.25 ? jitter * 0.6 : 0;
        const back = ease(clamp01((u - SHRINK_END - lag * 0.4) / (TRAVEL_END - SHRINK_END)));
        const trail = new THREE.Vector3(
          chestFrom.x + (chestTo.x - chestFrom.x) * back,
          CHEST + Math.sin(Math.PI * back) * 12,
          -Math.sin(Math.PI * back) * BEHIND,
        );
        p.copy(ballPoint(lag > 0 ? trail : ball, seed, lag > 0 ? BALL * 2.5 : BALL));
      } else {
        // раскрытие: от центра к контуру — ближние к груди точки ложатся первыми
        this.bodyPoint(index, jump.toX, jump.toFacing, to);
        const distance = to.distanceTo(chestTo) / 110;
        const s = ease(clamp01(((u - TRAVEL_END) / (1 - TRAVEL_END)) * 1.4 - distance * 0.5));
        p.lerpVectors(ballPoint(chestTo, seed, BALL), to, s);
      }
      this.positions[index * 3] = p.x;
      this.positions[index * 3 + 1] = p.y;
      this.positions[index * 3 + 2] = p.z;
      // чешуйка: цвет бойца, блик мерцает по кадру; в шаре — ярче и теплее к белому
      const inBall = u >= SHRINK_END * 0.7 && u < TRAVEL_END + 0.12;
      const sparkle = ((seed >>> 4) + tick * 7) % 23 === 0 ? 1.8 : 1;
      color.copy(this.body).multiplyScalar((inBall ? 1.6 : 1.1) * sparkle);
      if (inBall) color.lerp(new THREE.Color(0xf4e8ff), 0.35);
      this.colors[index * 3] = color.r;
      this.colors[index * 3 + 1] = color.g;
      this.colors[index * 3 + 2] = color.b;
    }
    const geometry = this.points.geometry;
    const position = geometry.getAttribute("position");
    const tint = geometry.getAttribute("color");
    position.needsUpdate = true;
    tint.needsUpdate = true;
  }
}

/** Точка внутри шара радиуса r вокруг c — своя для каждой частицы. */
function ballPoint(center: THREE.Vector3, seed: number, radius: number): THREE.Vector3 {
  const a = ((seed >>> 3) & 1023) * 0.00614;
  const b = ((seed >>> 13) & 1023) * 0.00307;
  const r = radius * Math.cbrt(((seed >>> 23) & 255) / 255);
  return new THREE.Vector3(
    center.x + r * Math.sin(b) * Math.cos(a),
    center.y + r * Math.cos(b),
    center.z + r * Math.sin(b) * Math.sin(a),
  );
}
